import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingSmall} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslate} from "@repo/i18n";
import {RequireReauth} from "@repo/user-settings";

import {DisableTotpTwoFactorModal} from "./disable-totp-two-factor-modal.tsx";
import {EnableTotpTwoFactorModal} from "./enable-totp-two-factor-modal.tsx";
import {RecoveryCodesTwoFactorModal} from "./recovery-codes-two-factor-modal.tsx";

type Props = {
    /* The KAID of the user. */
    kaid: string;
    /* The email of the user, used to identify the account in the user's
    authenticator app. */
    email: string;
    /* Whether the user has enabled two-factor authentication. TOTP two-factor
    authentication refers to using an authenticator app as a second factor,
    and is currently the only support form of 2FA. */
    isTotpTwoFactorEnabled: boolean;
    /* A function to call when the user has successfully changed their settings.
    Pops up a toast and refreshes the page. */
    onSettingsChanged: () => unknown;
};

export function SecuritySettings({
    kaid,
    email,
    isTotpTwoFactorEnabled,
    onSettingsChanged,
}: Props): React.ReactElement {
    const [reauthSuccessfulRegenerate, setReauthSuccessfulRegenerate] =
        React.useState(false);
    const [reauthSuccessfulEnableDisable, setReauthSuccessfulEnableDisable] =
        React.useState(false);

    return (
        <View role="region" aria-label={doNotTranslate`Security settings`}>
            <HeadingSmall style={styles.header} tag="h2">
                {doNotTranslate`Security`}
            </HeadingSmall>
            <Strut size={spacing.medium_16} />
            <Body>
                {isTotpTwoFactorEnabled
                    ? doNotTranslate`Two-factor authentication with an authenticator app is enabled.`
                    : doNotTranslate`Enabling two-factor authentication adds an extra layer of security by asking you to input a six digit code from an authenticator app when you log in.`}
            </Body>
            <Strut size={spacing.small_12} />
            {isTotpTwoFactorEnabled && (
                <React.Fragment>
                    <RequireReauth
                        identifier={kaid}
                        onSuccess={() => setReauthSuccessfulRegenerate(true)}
                        onFailure={() => {}}
                    >
                        {(invokeReauth) => (
                            <React.Fragment>
                                <ModalLauncher
                                    opened={reauthSuccessfulRegenerate}
                                    onClose={() =>
                                        setReauthSuccessfulRegenerate(false)
                                    }
                                    modal={
                                        <RecoveryCodesTwoFactorModal
                                            onSettingsChanged={
                                                onSettingsChanged
                                            }
                                            regenerating={true}
                                        />
                                    }
                                />
                                <Button
                                    onClick={invokeReauth}
                                    style={styles.button}
                                    size="small"
                                >
                                    {doNotTranslate`Regenerate two-factor authentication recovery codes`}
                                </Button>
                            </React.Fragment>
                        )}
                    </RequireReauth>
                    <Strut size={spacing.small_12} />
                </React.Fragment>
            )}
            <RequireReauth
                identifier={kaid}
                onSuccess={() => setReauthSuccessfulEnableDisable(true)}
                onFailure={() => {}}
            >
                {(invokeReauth) => (
                    <React.Fragment>
                        <ModalLauncher
                            opened={reauthSuccessfulEnableDisable}
                            onClose={() =>
                                setReauthSuccessfulEnableDisable(false)
                            }
                            modal={
                                isTotpTwoFactorEnabled ? (
                                    <DisableTotpTwoFactorModal
                                        kaid={kaid}
                                        onSettingsChanged={onSettingsChanged}
                                    />
                                ) : (
                                    <EnableTotpTwoFactorModal
                                        email={email}
                                        onSettingsChanged={onSettingsChanged}
                                    />
                                )
                            }
                        />
                        <Button
                            onClick={invokeReauth}
                            style={styles.button}
                            actionType={
                                isTotpTwoFactorEnabled
                                    ? "destructive"
                                    : "progressive"
                            }
                            size="small"
                        >
                            {isTotpTwoFactorEnabled
                                ? doNotTranslate`Disable two-factor authentication`
                                : doNotTranslate`Enable two-factor authentication`}
                        </Button>
                    </React.Fragment>
                )}
            </RequireReauth>
        </View>
    );
}

const styles = StyleSheet.create({
    header: {
        color: color.offBlack,
    },
    button: {
        alignSelf: "flex-start",
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,
    },
});
